import { UserApiEntity } from 'access/modules/UserModule/UserModule.types';
import features from 'system/features';
import { LogUtils } from 'utils/logUtils';

const isEnabled = () => features.isEnabled('SENTRY');

function setUser(user: UserApiEntity, pid?: string) {
    if (!isEnabled() || !window.Sentry) {
        return;
    }

    window.Sentry.setUser({
        email: user.email,
        id: String(user.uid),
        username: user.name,
        project: pid
            ? user.projects.find((item) => item.pid === pid)
            : undefined,
    });
}

function captureApiResponse(logData) {
    if (!isEnabled() || !window.Sentry) {
        return;
    }
    if (logData.status < 200 || logData.status >= 400) {
        window.Sentry.configureScope((scope) => {
            scope.setExtras(logData);
            scope.setFingerprint([
                'api-fail',
                logData.shortenedMethod,
                logData.status,
            ]);
            const backendType = logData[LogUtils.X_BACKEND_TYPE];
            scope.setTags({
                'api-method': logData.shortenedMethod,
                status: logData.status,
                backendType,
            });

            const errorMessage = `STATUS: ${logData.status} ${
                logData.shortenedMethod
            } (email: ${logData['x-email']}, xReqId: ${
                logData.xReqId
            }, X-MCS-Request-Id: ${logData[LogUtils.X_MCS_REQUEST_ID]})`;

            window.Sentry.captureException(getError(backendType, errorMessage));
        });
    }
}

export enum BackendTypes {
    OpenStack = 'OpenStack',
    RPC = 'RPC',
    S3 = 'S3',
}
function getError(backendType: BackendTypes, errorMessage: string) {
    switch (backendType) {
        case BackendTypes.OpenStack:
            return new OpenStackError(errorMessage);
        case BackendTypes.RPC:
            return new RPCError(errorMessage);
        case BackendTypes.S3:
            return new S3Error(errorMessage);
        default:
            return new Error(errorMessage);
    }
}

export class RoutingError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RoutingError';
    }
}

class OpenStackError extends Error {
    constructor(message) {
        super(message);
        this.name = 'OpenStackError';
    }
}

class S3Error extends Error {
    constructor(message) {
        super(message);
        this.name = 'S3Error';
    }
}

class RPCError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RPCError';
    }
}

function empty() {}

export const SentryUtils = {
    setUser,
    captureApiResponse,
    configureScope: window.Sentry?.configureScope || empty,
    captureException: window.Sentry?.captureException || empty,
    captureEvent: window.Sentry?.captureEvent || empty,
    captureMessage: window.Sentry?.captureMessage || empty,
};
